import { useState, useRef, useEffect, useMemo, KeyboardEvent } from "react";
import { Send, Bot, ChevronRight, ChevronLeft, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useChat } from "@/hooks/useChat";
import type { PARInvoiceRow } from "@/lib/par/csvInvoiceParser";
import {
  buildChatContext,
  buildEmptyChatContext,
  type ChatPaymentRunSummary,
} from "@/lib/buildChatContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface ChatPanelProps {
  page: string;
  invoices?: PARInvoiceRow[];
  runs?: ChatPaymentRunSummary[];
  /** Called whenever the collapsed state changes — passes the new collapsed value. */
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Called when the user clicks "Refresh" on the staleness chip. */
  onRefresh?: () => void;
}

const PAR_SAMPLE_PROMPTS = [
  "Which vendor has the most missed early pay opportunities?",
  "How much cash would I need to capture all early pay discounts?",
  "Which invoices expire soonest?",
  "What's my total potential savings this month?",
  "Which vendors give the best discount rates?",
  "Show me invoices over $50k with early pay terms",
];

const GENERIC_SAMPLE_PROMPTS = [
  "What's my total outstanding balance?",
  "How many vendors do I have?",
  "What are my early payment opportunities?",
  "Which invoices are due soonest?",
];

export function ChatPanel({ page, invoices, runs, onCollapsedChange, onRefresh }: ChatPanelProps) {
  const [inputValue, setInputValue] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const context = useMemo(
    () =>
      invoices && invoices.length > 0
        ? buildChatContext(invoices, page, runs)
        : buildEmptyChatContext(page),
    [invoices, page, runs]
  );

  // Staleness detection: show a chip when invoice data changes after the first message
  const initialVersionRef = useRef<string | null>(null);
  const [isStale, setIsStale] = useState(false);

  const { messages, loading, sendMessage, clearMessages } = useChat(context);

  const samplePrompts = page === "par-systems" ? PAR_SAMPLE_PROMPTS : GENERIC_SAMPLE_PROMPTS;

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Detect when invoice data changes after the first message is sent
  useEffect(() => {
    if (messages.length === 0) return;
    if (!initialVersionRef.current) {
      initialVersionRef.current = context.contextVersion;
      return;
    }
    if (context.contextVersion !== initialVersionRef.current) {
      setIsStale(true);
    }
  }, [context.contextVersion, messages.length]);

  const handleToggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    onCollapsedChange?.(next);
  };

  const handleSend = () => {
    if (!inputValue.trim()) return;
    sendMessage(inputValue);
    setInputValue("");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSamplePrompt = (prompt: string) => {
    sendMessage(prompt);
  };

  return (
    /* Fixed right sidebar — 25vw expanded, ~40px collapsed. Smooth CSS transition. */
    <div
      className={cn(
        "fixed top-0 right-0 z-50 h-full flex flex-col border-l border-border bg-background shadow-2xl",
        "transition-[width] duration-300 ease-in-out",
        collapsed ? "w-10" : "w-[25vw]"
      )}
      aria-label="DiscoRun AI chat"
    >
      {/* Toggle tab — sits on the left edge of the panel */}
      <button
        onClick={handleToggle}
        aria-label={collapsed ? "Expand chat panel" : "Collapse chat panel"}
        className={cn(
          "absolute top-1/2 -translate-y-1/2 -left-3 z-10",
          "flex items-center justify-center",
          "h-12 w-6 rounded-l-md",
          "bg-background border border-r-0 border-border shadow-sm",
          "hover:bg-muted transition-colors",
          "text-muted-foreground hover:text-foreground"
        )}
      >
        {collapsed ? (
          <ChevronLeft className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
      </button>

      {/* Panel contents — hidden when collapsed */}
      <div
        className={cn(
          "flex flex-col flex-1 min-h-0 overflow-hidden",
          "transition-opacity duration-200",
          collapsed ? "opacity-0 pointer-events-none" : "opacity-100"
        )}
      >
        {/* Header */}
        <div className="flex items-center px-4 py-3 border-b border-border bg-muted/30 shrink-0">
          <Bot className="h-4 w-4 text-primary" />
          <span className="ml-2 text-sm font-medium flex-1">DiscoRun AI</span>
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-foreground"
              onClick={clearMessages}
              aria-label="Clear chat"
              title="Clear chat"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {/* Message area */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-0">
          {messages.length === 0 ? (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground text-center pt-2">
                Ask a question about your invoice data
              </p>
              <div className="flex flex-wrap gap-1.5">
                {samplePrompts.map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => handleSamplePrompt(prompt)}
                    className="inline-flex items-center rounded-full border border-border bg-muted/50 hover:bg-muted px-2.5 py-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors cursor-pointer text-left"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={cn("flex", msg.role === "user" ? "justify-end" : "justify-start")}
                >
                  <div
                    className={cn(
                      "max-w-[80%] rounded-xl px-3 py-2 text-sm leading-relaxed",
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-foreground"
                    )}
                  >
                    {msg.role === "user" ? (
                      msg.content
                    ) : (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
                          strong: ({ children }) => (
                            <strong className="font-semibold text-foreground">{children}</strong>
                          ),
                          em: ({ children }) => <em className="italic">{children}</em>,
                          ul: ({ children }) => (
                            <ul className="list-disc pl-4 mb-1.5 space-y-0.5">{children}</ul>
                          ),
                          ol: ({ children }) => (
                            <ol className="list-decimal pl-4 mb-1.5 space-y-0.5">{children}</ol>
                          ),
                          li: ({ children }) => <li className="leading-snug">{children}</li>,
                          h1: ({ children }) => (
                            <h1 className="text-base font-bold mb-1 mt-2 first:mt-0">{children}</h1>
                          ),
                          h2: ({ children }) => (
                            <h2 className="text-sm font-bold mb-1 mt-2 first:mt-0">{children}</h2>
                          ),
                          h3: ({ children }) => (
                            <h3 className="text-sm font-semibold mb-0.5 mt-1.5 first:mt-0">
                              {children}
                            </h3>
                          ),
                          code: ({ children, className }) => {
                            const isBlock = className?.includes("language-");
                            return isBlock ? (
                              <code className="block bg-background/60 rounded px-2 py-1.5 text-xs font-mono my-1.5 overflow-x-auto whitespace-pre">
                                {children}
                              </code>
                            ) : (
                              <code className="bg-background/60 rounded px-1 py-0.5 text-xs font-mono">
                                {children}
                              </code>
                            );
                          },
                          pre: ({ children }) => (
                            <pre className="my-1.5 overflow-x-auto">{children}</pre>
                          ),
                          table: ({ children }) => (
                            <div className="overflow-x-auto my-1.5">
                              <table className="w-full text-xs border-collapse border border-border/50">
                                {children}
                              </table>
                            </div>
                          ),
                          thead: ({ children }) => (
                            <thead className="bg-background/40">{children}</thead>
                          ),
                          th: ({ children }) => (
                            <th className="border border-border/50 px-2 py-1 text-left font-semibold">
                              {children}
                            </th>
                          ),
                          td: ({ children }) => (
                            <td className="border border-border/50 px-2 py-1">{children}</td>
                          ),
                          blockquote: ({ children }) => (
                            <blockquote className="border-l-2 border-border pl-2 my-1 text-muted-foreground italic">
                              {children}
                            </blockquote>
                          ),
                          hr: () => <hr className="border-border/50 my-2" />,
                          a: ({ href, children }) => (
                            <a
                              href={href}
                              className="text-primary underline underline-offset-2 hover:opacity-80"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {children}
                            </a>
                          ),
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    )}
                  </div>
                </div>
              ))}

              {/* Loading indicator */}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-xl px-3 py-2">
                    <span className="flex gap-1 items-center">
                      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:0ms]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:150ms]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:300ms]" />
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Staleness chip */}
        {isStale && (
          <div className="shrink-0 mx-3 mb-2 flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
            <span>Invoice data has changed.</span>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => {
                  onRefresh?.();
                  setIsStale(false);
                  initialVersionRef.current = null; // reset so fresh data sets new baseline
                }}
                className="font-medium underline underline-offset-2 hover:opacity-80"
              >
                Refresh
              </button>
              <button
                onClick={() => setIsStale(false)}
                aria-label="Dismiss"
                className="hover:opacity-80"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Input area */}
        <div className="border-t border-border px-3 py-2.5 shrink-0 flex gap-2 items-center bg-background">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your data..."
            className="flex-1 h-8 text-sm"
            disabled={loading}
          />
          <Button
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={handleSend}
            disabled={!inputValue.trim() || loading}
            aria-label="Send message"
          >
            <Send className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
